import { useMutation, useQueryClient, type QueryKey, type UseMutationOptions } from "@tanstack/react-query";
import useDialog from "@hooks/useDialog";

// Using a generic type for optimistic updates to allow for different data structures (e.g., arrays, objects)
type OptimisticUpdateFn<TData, TQueryData = TData, TVariables = unknown> = (
  oldData: TQueryData | undefined,
  variables: TVariables,
) => TQueryData | undefined;

interface UseDataMutationProps<TData, TQueryData = TData[] | undefined, TVariables = unknown, TContext = unknown> {
  mutationFn: (variables: TVariables) => Promise<TData | null>;
  queryKeyToInvalidate: QueryKey;
  successMessage?: string;
  errorMessage?: string;
  optimisticUpdateFn?: OptimisticUpdateFn<TData[] | undefined, TQueryData, TVariables>;
  options?: Omit<UseMutationOptions<TData | null, Error, TVariables, TContext>, 'mutationFn'>;
}

export function useDataMutation<TData = unknown, TQueryData = TData[] | undefined, TVariables = unknown, TContext = unknown>({
  mutationFn,
  queryKeyToInvalidate,
  successMessage,
  errorMessage,
  optimisticUpdateFn,
  options,
}: UseDataMutationProps<TData, TQueryData, TVariables, TContext>) {
  const queryClient = useQueryClient();
  const { showSuccessSnackbar, showErrorSnackbar } = useDialog();

  return useMutation({
    mutationFn,
    ...options,
    async onMutate(variables) {
      if (options?.onMutate) {
        return options.onMutate(variables);
      }

      if (!optimisticUpdateFn) return;

      await queryClient.cancelQueries({ queryKey: queryKeyToInvalidate });

      const previousData = queryClient.getQueryData(queryKeyToInvalidate);

      queryClient.setQueryData(
        queryKeyToInvalidate,
        (old: TQueryData) => {
          if (typeof old === "object" && !!old && "pages" in old && "pageParams" in old && Array.isArray(old.pages)) {
            return {
              ...old,
              pages: old.pages?.map((page) => optimisticUpdateFn(page, variables))
            }
          }
          return optimisticUpdateFn(old, variables)
        },
      );

      return { previousData } as any;
    },
    onError: (err, variables, context: any) => {
      if (context?.previousData && optimisticUpdateFn) {
        queryClient.setQueryData(queryKeyToInvalidate, context.previousData);
      }
      showErrorSnackbar(errorMessage || (err as Error).message || "An error occurred");

      if (options?.onError) {
        options.onError(err, variables, context);
      }
    },
    onSuccess: (data, variables, context) => {
      if (successMessage) {
        showSuccessSnackbar(successMessage);
      }
      // log({
      //   eventtype: "MutationSuccess",
      //   name: `Mutation ${queryKeyToInvalidate.join("-")} Succeeded`,
      //   payload: {
      //     queryKey: queryKeyToInvalidate,
      //     variables,
      //     data,
      //   },
      // });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onSettled: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate, exact: false });
      if (options?.onSettled) {
        options.onSettled(...args);
      }
    },
  });
}
